import { useEffect } from 'react';

type UseOnDetectOutsideClickProps = {
	/**
	 * Qualifiers are used to determine if the outside click should be detected.
	 * If all qualifiers are true, the outside click will be detected.
	 */
	qualifiers: boolean[];
	/**
	 * If any of the refs contain the event target, the outside click will not be detected.
	 */
	refs: React.RefObject<HTMLElement>[];
	/**
	 * onOutsideClick is the function that will be called when the outside click is detected.
	 */
	onOutsideClick: () => void;
};

export const useOnDetectOutsideClick = ({
	qualifiers,
	refs,
	onOutsideClick,
}: UseOnDetectOutsideClickProps) => {
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				qualifiers.every((qualifier) => qualifier) &&
				!refs.some((ref) => ref?.current?.contains(event.target as Node))
			) {
				/*
				 * If the click is on any atlaskit portal, we don't want to trigger the outside click handler.
				 * This is to prevent the popper from closing when the user interacts with any components
				 * that are rendered in a portal from a child component (e.g. modals, dropdowns).
				 * There is no robust way to detect that the portal was rendered from a child component
				 * so we will just ignore all clicks on atlaskit portals.
				 */
				const portalElement = (event.target as HTMLElement).closest('.atlaskit-portal');
				if (portalElement) {
					return;
				}
				onOutsideClick();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [qualifiers, refs, onOutsideClick]);
};
